import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CarrierBidScope } from '@magimp/shared';
import { Button, Input, Select } from '../../../../ui/index.js';
import { INSURANCE_PERCENT_DEFAULT } from '../../../../api/requests/index.js';
import type { CarrierInsuranceQuote, RequestKind, RequestView } from '../../types.js';

export interface CarrierInsuranceScopePickerProps {
  request: RequestView;
  existingQuotes: CarrierInsuranceQuote[];
  pending: boolean;
  submitError: string | null;
  onSubmit: (input: {
    scope: CarrierBidScope;
    scopeRefId: string | null;
    percent: number;
    comment: string;
  }) => void;
  onCancel: () => void;
}

interface ScopeOption {
  scope: CarrierBidScope;
  scopeRefId: string | null;
  label: string;
}

// INVARIANT (#1163): CONTAINER kind has no `RequestBox` rows, so
//   `BOX` / `BOX_ITEM` / `BOX_GROUP` are unresolvable by construction.
//   Hide them at the picker level so a user can't pick a scope the
//   service is guaranteed to reject.
const CONTAINER_ALLOWED: ReadonlySet<CarrierBidScope> = new Set(['REQUEST', 'ITEM']);

function isScopeAllowedForKind(scope: CarrierBidScope, kind: RequestKind): boolean {
  if (kind === 'CONTAINER') return CONTAINER_ALLOWED.has(scope);
  return true;
}

/**
 * @aiPurpose inline scope picker for adding one carrier insurance
 *   quote (#1163). Lists every legal `(scope, scopeRefId)` target,
 *   filtering out combinations already occupied by `existingQuotes`
 *   so the user cannot create a duplicate. Mirrors the rates per-scope
 *   targeting model. CONTAINER kind hides BOX-family scopes.
 * @aiInvariant Primary REQUEST option is always offered first when no
 *   primary exists yet; when a primary exists the user sees override
 *   options keyed by sub-resource id. The service double-checks every
 *   pick (primary-first, cross-request leak, kind-allowed) — the
 *   picker is a UX guard, not a security boundary.
 */
export function CarrierInsuranceScopePicker({
  request,
  existingQuotes,
  pending,
  submitError,
  onSubmit,
  onCancel,
}: CarrierInsuranceScopePickerProps) {
  const { t } = useTranslation(['requests', 'common']);

  const options = useMemo<ScopeOption[]>(() => {
    const used = new Set<string>();
    for (const q of existingQuotes) used.add(`${q.scope}::${q.scopeRefId ?? ''}`);
    const out: ScopeOption[] = [];
    const hasPrimary = existingQuotes.some((q) => q.scope === 'REQUEST');
    if (!hasPrimary) {
      out.push({
        scope: 'REQUEST',
        scopeRefId: null,
        label: t('requests:carriers.insurance.scope.request.label'),
      });
    }
    if (!hasPrimary) {
      // INVARIANT (#1163): override creation requires an existing
      //   primary. The service rejects with primary-required; the
      //   picker hides override rows until a primary exists.
      return out;
    }
    // ITEM scope — always available on STANDARD and CONTAINER.
    for (const item of request.items) {
      if (used.has(`ITEM::${item.id}`)) continue;
      out.push({
        scope: 'ITEM',
        scopeRefId: item.id,
        label: t('requests:carriers.insurance.scope.item.label', { name: item.name }),
      });
    }
    if (!isScopeAllowedForKind('BOX', request.kind)) return out;
    const boxes = request.boxes ?? [];
    boxes.forEach((box, idx) => {
      const boxNo = idx + 1;
      if (!used.has(`BOX::${box.id}`)) {
        out.push({
          scope: 'BOX',
          scopeRefId: box.id,
          label: t('requests:carriers.insurance.scope.box.label', { n: boxNo }),
        });
      }
      // BOX_ITEM per actual box item.
      for (const bi of box.items) {
        if (used.has(`BOX_ITEM::${bi.id}`)) continue;
        out.push({
          scope: 'BOX_ITEM',
          scopeRefId: bi.id,
          label: t('requests:carriers.insurance.scope.boxItem.label', {
            n: boxNo,
            name: bi.name,
          }),
        });
      }
      // BOX_GROUP per distinct groupOrdinal.
      const ordinals = new Set<number>();
      for (const bi of box.items) ordinals.add(bi.groupOrdinal);
      for (const ord of ordinals) {
        const ref = `${box.id}:${ord}`;
        if (used.has(`BOX_GROUP::${ref}`)) continue;
        out.push({
          scope: 'BOX_GROUP',
          scopeRefId: ref,
          label: t('requests:carriers.insurance.scope.boxGroup.label', { n: boxNo, k: ord }),
        });
      }
    });
    return out;
  }, [existingQuotes, request, t]);

  const [optionKey, setOptionKey] = useState<string>(
    options[0] ? `${options[0].scope}::${options[0].scopeRefId ?? ''}` : '',
  );
  const [percentStr, setPercentStr] = useState<string>(String(INSURANCE_PERCENT_DEFAULT));
  const [comment, setComment] = useState<string>('');
  const [localError, setLocalError] = useState<string | null>(null);

  const selected = options.find((o) => `${o.scope}::${o.scopeRefId ?? ''}` === optionKey);

  function handleSubmit() {
    setLocalError(null);
    if (!selected) {
      setLocalError(t('requests:carriers.insurance.scopePicker.required'));
      return;
    }
    const raw = percentStr.trim();
    const percent = raw === '' ? INSURANCE_PERCENT_DEFAULT : Number(raw);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      setLocalError(t('requests:carriers.insurance.errors.percentBounds'));
      return;
    }
    onSubmit({
      scope: selected.scope,
      scopeRefId: selected.scopeRefId,
      percent,
      comment: comment.trim(),
    });
  }

  if (options.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>
        {t('requests:carriers.insurance.scopePicker.noOptions')}
        <Button kind="ghost" size="sm" onClick={onCancel} style={{ marginLeft: 8 }}>
          {t('common:actions.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid="carrier-insurance-scope-picker"
      style={{ display: 'grid', gap: 6, fontSize: 12, paddingBlock: 4 }}
    >
      <Select
        value={optionKey}
        onChange={(v) => setOptionKey(v)}
        placeholder={t('requests:carriers.insurance.scopePicker.placeholder')}
        options={options.map((o) => ({
          value: `${o.scope}::${o.scopeRefId ?? ''}`,
          label: o.label,
        }))}
      />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ display: 'inline-flex', gap: 6, color: 'var(--fg-2)' }}>
          <span>{t('requests:carriers.insurance.percentLabel')}</span>
          <span style={{ width: 90 }}>
            <Input type="number" value={percentStr} onChange={setPercentStr} />
          </span>
        </label>
        <label
          style={{ flex: 1, minWidth: 220, display: 'inline-flex', gap: 6, color: 'var(--fg-2)' }}
        >
          <span>{t('requests:carriers.insurance.commentLabel')}</span>
          <span style={{ flex: 1 }}>
            <Input
              value={comment}
              onChange={setComment}
              placeholder={t('requests:carriers.insurance.commentPlaceholder')}
              maxLength={2000}
            />
          </span>
        </label>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <Button kind="primary" size="sm" disabled={pending} onClick={handleSubmit}>
          {t('common:actions.save')}
        </Button>
        <Button kind="ghost" size="sm" disabled={pending} onClick={onCancel}>
          {t('common:actions.cancel')}
        </Button>
      </div>
      {(localError ?? submitError) !== null && (
        <div style={{ color: 'var(--status-error-fg)' }}>{localError ?? submitError}</div>
      )}
    </div>
  );
}
